import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { ClipboardDocumentListIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/solid';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import ModalFlowPreliminaryTransaction from './ModalFlowPreliminaryTransaction';
import ModalDeletePreliminaryBatch from './ModalDeletePreliminaryBatch';
import { fetchFlowPreliminaryTransactions } from 'views/finance/moduleServices/flow_requests';

const CardFlowPreliminaryTransactions = forwardRef((props, ref) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRecord, setModalRecord] = useState(null);
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchFlowPreliminaryTransactions();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load preliminary transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useImperativeHandle(ref, () => ({ refresh: load }), [load]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setModalRecord(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalRecord(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalRecord(null);
  };

  const formatHourCell = (h) => {
    if (!h) return '—';
    if (typeof h === 'string') return h.length >= 8 ? h.slice(0, 8) : h;
    return '—';
  };

  const sourceTooltip = (row) => {
    const src = row.source?.trim() || '—';
    const ext = row.external_id?.trim() || '—';
    return `${src} | ${ext}`;
  };

  return (
    <>
      <div className="h-auto bg-white shadow rounded-xl flex flex-col flex-1 overflow-hidden">
        <div className="rounded-t-xl overflow-hidden">
          <div className="bg-green-800 px-3 py-1.5 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <ClipboardDocumentListIcon className="h-4 w-4 text-white shrink-0" />
              <span className="text-sm font-semibold text-white truncate">Preliminary flow transactions</span>
              <span className="text-xs text-white opacity-70 shrink-0">({rows.length})</span>
            </div>
            <button
              type="button"
              onClick={openCreate}
              disabled={loading}
              className="inline-flex items-center gap-1.5 shrink-0 rounded-lg border border-white/40 bg-white/10 px-2.5 py-1 text-xs font-semibold tracking-wide text-white shadow-sm backdrop-blur-sm transition-all duration-150 hover:border-white/60 hover:bg-white/20 hover:shadow active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40"
              title="Add preliminary transaction"
            >
              <PlusIcon className="h-3.5 w-3.5 opacity-95" aria-hidden />
              Add
            </button>
          </div>
        </div>

        <div className="p-4 pt-3 flex flex-col gap-3">
          <p className="text-[11px] leading-snug text-gray-500">
            Staging area: same fields as Flow transactions, plus status and source. Hover the (i) icon for source
            and external id. Merge into official flows will be added later.
          </p>

          {loading ? (
            <div className="flex justify-center py-8">
              <SpinnerLoad />
            </div>
          ) : (
            <div className="overflow-x-auto max-h-96 border border-gray-200 rounded-lg">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Account</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Date</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Hour</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Desc</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Type</th>
                    <th className="px-2 py-1.5 text-right font-semibold text-gray-600">Value</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Curr</th>
                    <th className="px-2 py-1.5 text-right font-semibold text-gray-600">Std</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">L0</th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-600">Status</th>
                    <th className="px-2 py-1.5 text-center font-semibold text-gray-600 w-10">Src</th>
                    <th className="px-2 py-1.5 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-2 py-6 text-center text-gray-500">
                        No preliminary rows yet. Use <strong>Add</strong> in the header.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1 truncate max-w-[7rem]" title={row.flow_account || ''}>
                          {row.flow_account || '—'}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">{row.date}</td>
                        <td className="px-2 py-1 whitespace-nowrap tabular-nums">{formatHourCell(row.hour)}</td>
                        <td className="px-2 py-1 max-w-[10rem] truncate" title={row.desc}>
                          {row.desc}
                        </td>
                        <td className="px-2 py-1">{row.flow_type}</td>
                        <td className="px-2 py-1 text-right">{row.flow_value}</td>
                        <td className="px-2 py-1">{row.flow_currency}</td>
                        <td className="px-2 py-1 text-right">{row.std_value}</td>
                        <td className="px-2 py-1 truncate max-w-[6rem]" title={row.categ_l0}>
                          {row.categ_l0}
                        </td>
                        <td className="px-2 py-1 truncate max-w-[5rem]" title={row.status}>
                          {row.status || '—'}
                        </td>
                        <td className="px-2 py-1 text-center">
                          <button
                            type="button"
                            className="inline-flex justify-center p-0.5 rounded text-gray-500 hover:text-green-700 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-green-600"
                            title={sourceTooltip(row)}
                            aria-label={`Source and external id: ${sourceTooltip(row)}`}
                          >
                            <InformationCircleIcon className="h-4 w-4" aria-hidden />
                          </button>
                        </td>
                        <td className="px-1 py-1">
                          <button
                            type="button"
                            onClick={() => openEdit(row)}
                            className="p-1 rounded text-green-800 hover:bg-green-100"
                            title="Edit"
                          >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setBatchDeleteOpen(true)}
              disabled={loading}
              className="w-full sm:w-auto px-3 py-2 rounded-xl border border-red-200 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Delete preliminary data…
            </button>
          </div>
        </div>
      </div>

      <ModalDeletePreliminaryBatch
        isOpen={batchDeleteOpen}
        onClose={() => setBatchDeleteOpen(false)}
        onDeleted={load}
      />

      <ModalFlowPreliminaryTransaction
        isOpen={modalOpen}
        record={modalRecord}
        onClose={closeModal}
        onSaved={load}
      />
    </>
  );
});

CardFlowPreliminaryTransactions.displayName = 'CardFlowPreliminaryTransactions';

export default CardFlowPreliminaryTransactions;
